"use client";

import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/language-context';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ShieldCheck, Mail } from 'lucide-react';

interface TwoFactorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (code: string, otpId?: string | null) => Promise<void>;
  method: 'totp' | 'email';
  email: string;
  amount?: number;
}

export default function TwoFactorModal({
  isOpen,
  onClose,
  onVerify,
  method,
  email,
  amount
}: TwoFactorModalProps) {
  const { t, language } = useLanguage();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [otpId, setOtpId] = useState<string | null>(null);

  // Countdown para reenvío de email
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // NO auto-enviar email - usuario debe solicitarlo manualmente con botón "Enviar código"
  // Esto evita spam de emails cada vez que se abre el modal

  const handleSendEmailCode = async () => {
    if (sendingEmail || countdown > 0) return;

    setSendingEmail(true);
    setError('');

    try {
      const response = await fetch('/api/2fa/send-email-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          purpose: 'transaction',
          language
        })
      });

      const data = await response.json();

      if (data.success) {
        setEmailSent(true);
        setCountdown(60); // 60 segundos antes de poder reenviar
        // Guardar otpId para verificación posterior
        if (data.data?.otpId) {
          setOtpId(data.data.otpId);
        }
      } else {
        setError(data.error || 'Error enviando código');
      }
    } catch (err: any) {
      console.error('Error sending email code:', err);
      setError('Error enviando código por email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      setError(t('settings.security.enter_valid_code') || 'Por favor ingresa un código de 6 dígitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Pasar otpId solo si es método email
      await onVerify(code, method === 'email' ? otpId : undefined);
      // Si llega aquí, la verificación fue exitosa
      setCode('');
      onClose();
    } catch (err: any) {
      console.error('Error verifying 2FA code:', err);
      setError(err.message || t('security.invalid_2fa_code') || 'Código inválido o expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (value: string) => {
    // Solo números, máximo 6 dígitos
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setCode(numericValue);
    setError('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code.length === 6) {
      handleVerify();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {t('admin.settings.security.2fa_required') || 'Autenticación Requerida'}
          </DialogTitle>
          <DialogDescription>
            {amount && (
              <span className="block mb-2 font-semibold text-foreground">
                {t('admin.settings.security.transaction_amount') || 'Monto'}: {amount.toFixed(2)} USDT
              </span>
            )}
            {method === 'totp' 
              ? (t('admin.settings.security.enter_authenticator_code') || 'Ingresa el código de 6 dígitos de tu aplicación Google Authenticator')
              : (t('admin.settings.security.enter_email_code') || 'Ingresa el código de 6 dígitos enviado a tu correo electrónico')
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Input de código */}
          <div className="space-y-2">
            <label htmlFor="2fa-code" className="text-sm font-medium">
              {t('admin.settings.security.verification_code') || 'Código de verificación'}
            </label>
            <Input
              id="2fa-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="000000"
              className="text-center text-2xl tracking-widest font-mono"
              autoFocus
              disabled={loading}
            />
          </div>

          {/* Botón de reenvío para email */}
          {method === 'email' && (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSendEmailCode}
                disabled={sendingEmail || countdown > 0}
                className="text-xs"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    {t('admin.settings.security.sending') || 'Enviando...'}
                  </>
                ) : countdown > 0 ? (
                  <>
                    <Mail className="mr-2 h-3 w-3" />
                    {t('admin.settings.security.resend_in') || 'Reenviar en'} {countdown}s
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-3 w-3" />
                    {emailSent 
                      ? (t('admin.settings.security.resend_code') || 'Reenviar código')
                      : (t('admin.settings.security.send_code') || 'Enviar código')
                    }
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Mensaje de error */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Botones de acción */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              {t('common.cancel') || 'Cancelar'}
            </Button>
            <Button
              onClick={handleVerify}
              disabled={loading || code.length !== 6}
              className="flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('admin.settings.security.verifying') || 'Verificando...'}
                </>
              ) : (
                t('admin.settings.security.verify_and_continue') || 'Verificar y Continuar'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
